"use client";

import { Box, Flex, Icon, VStack } from "@chakra-ui/react";
import { HiOutlineChevronDown } from "react-icons/hi2";
import type { LinkGroup } from "@/components/shared/sidebar/linkItems";
import { SidebarNavCountBadge } from "@/components/shared/sidebar/SidebarNavCountBadge";
import type { MakeupNavBadge } from "@/components/shared/sidebar/makeupNavBadge";
import type { UserTasksNavBadge } from "@/components/shared/sidebar/userTasksNavBadge";
import {
  buildNavGroupHeaderBadgeLabel,
  getNavGroupHeaderBadgeCount,
} from "@/components/shared/sidebar/sidebarNavGroupBadge";
import { SidebarNavLinkItem } from "@/components/shared/sidebar/SidebarNavLinkItem";
import {
  SidebarNavIcon,
  SidebarNavItemLabel,
} from "@/components/shared/sidebar/SidebarNavItemLabel";
import type { useThemeTokens } from "@/lib/theme/tokens";

interface SidebarNavCollapsibleGroupProps {
  group: LinkGroup;
  isExpanded: boolean;
  onToggle: () => void;
  isMobile?: boolean;
  onNavigate?: () => void;
  controlBorderRadius: string;
  isClassic: boolean;
  theme: ReturnType<typeof useThemeTokens>;
  makeupNavBadge?: MakeupNavBadge | null;
  userTasksNavBadge?: UserTasksNavBadge | null;
}

export function SidebarNavCollapsibleGroup({
  group,
  isExpanded,
  onToggle,
  isMobile = false,
  onNavigate,
  controlBorderRadius,
  isClassic,
  theme,
  makeupNavBadge = null,
  userTasksNavBadge = null,
}: SidebarNavCollapsibleGroupProps) {
  if (!group.label || !group.id || group.items.length === 0) {
    return null;
  }

  const hasActiveChild = group.items.some((item) => item.active);
  const headerColor = hasActiveChild ? theme.accent.primary : theme.text.secondary;
  const headerBadgeCount = getNavGroupHeaderBadgeCount(
    group.items,
    makeupNavBadge,
    userTasksNavBadge,
  );
  const showHeaderCombinedBadge = !isExpanded && headerBadgeCount > 0;
  const childMakeupNavBadge = isExpanded ? makeupNavBadge : null;
  const childUserTasksNavBadge = isExpanded ? userTasksNavBadge : null;
  const headerAriaLabel = showHeaderCombinedBadge
    ? buildNavGroupHeaderBadgeLabel(group.label ?? "Group", headerBadgeCount)
    : undefined;

  return (
    <Box w="100%" data-testid={`sidebar-nav-group-${group.id}`}>
      <Flex
        as="button"
        type="button"
        onClick={onToggle}
        aria-expanded={isExpanded}
        aria-label={headerAriaLabel}
        w="100%"
        align="center"
        justify="space-between"
        gap={2}
        px={3}
        py={2.5}
        borderRadius={controlBorderRadius}
        bg="transparent"
        color={headerColor}
        transition="all 0.18s ease-out"
        sx={{
          "@media (hover: hover)": {
            "&:hover": {
              bg:
                theme.mode === "dark"
                  ? "rgba(255,255,255,0.08)"
                  : "rgba(11,13,16,0.08)",
              color: hasActiveChild ? theme.accent.primary : theme.text.primary,
              transform: "translateX(2px)",
            },
          },
        }}
      >
        <Flex align="center" gap={3} flex={1} minW={0}>
          {group.icon ? <SidebarNavIcon as={group.icon} /> : null}
          <SidebarNavItemLabel flex="1" minW={0} color={headerColor} textAlign={"left"}>
            {group.label}
          </SidebarNavItemLabel>
        </Flex>
        {showHeaderCombinedBadge ? (
          <SidebarNavCountBadge
            count={headerBadgeCount}
            data-testid="sidebar-nav-group-badge"
          />
        ) : null}
        <Icon
          as={HiOutlineChevronDown}
          boxSize={4}
          flexShrink={0}
          color={theme.text.tertiary}
          transition="transform 0.2s ease"
          transform={isExpanded ? "rotate(180deg)" : "rotate(0deg)"}
        />
      </Flex>

      <Box
        display="grid"
        gridTemplateRows={isExpanded ? "1fr" : "0fr"}
        transition="grid-template-rows 0.22s ease-out"
      >
        <Box
          minH={0}
          overflow="hidden"
          opacity={isExpanded ? 1 : 0}
          transform={isExpanded ? "translateY(0)" : "translateY(-6px)"}
          transition="opacity 0.22s ease-out, transform 0.22s ease-out"
          pointerEvents={isExpanded ? "auto" : "none"}
          aria-hidden={!isExpanded}
        >
          <VStack spacing={1} align="stretch" pl={4} pt={1} pb={2}>
            {group.items.map((item) => (
              <SidebarNavLinkItem
                key={item.href}
                item={item}
                isCollapsed={false}
                isMobile={isMobile}
                onNavigate={onNavigate}
                controlBorderRadius={controlBorderRadius}
                isClassic={isClassic}
                theme={theme}
                makeupNavBadge={childMakeupNavBadge}
                userTasksNavBadge={childUserTasksNavBadge}
              />
            ))}
          </VStack>
        </Box>
      </Box>
    </Box>
  );
}
